// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { onlyNumbers } from '@tectonics/prs/normalize';
import { useFieldMetadata } from '@tectonics/prs/form';
import { type UserInputValidationError } from '@tectonics/prs/validation';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { SimpleTextErrorMessage } from '@tectonics/look-and-feel/commons/validation-error-message';
import { useFormatRawForCardBrandMask } from '@tectonics/look-and-feel/commons/card-mask';
import { getCardBrandImageSrc } from '@tectonics/look-and-feel/commons/card-brand';
import { type Country } from '@tectonics/prs/country';
import { Field } from '../../../molecules';
import { Input, Label } from '../../../atoms';

export function FieldCardNumber(): Node {
  const inputId = useUniqueId();
  const [formatRaw, cardMask, cardBrand] = useFormatRawForCardBrandMask();
  const [value, handleChange] = useUserInputAsTextFieldWithFormatting('cardNumber', formatRaw, onlyNumbers);
  const labelMessage = useLabelMessage();
  const { submitting, validationError, formHasBeenSent } = useFieldMetadata('cardNumber');

  return (
    <Field
      data-test-name="card-number"
      label={<Label htmlFor={inputId}>{labelMessage}</Label>}
      showFloatingLabel={!!value}
      hasError={validationError && formHasBeenSent}
      error={<SimpleTextErrorMessage getMessage={getMessage} validationError={validationError} />}
    >
      <div style={{ position: 'relative' }}>
        <Input
          as={InputMask}
          data-secret
          mask={cardMask}
          id={inputId}
          onChange={handleChange}
          placeholder={labelMessage}
          readOnly={submitting}
          type="tel"
          value={value}
          name="card-number"
        />
        <img
          src={getCardBrandImageSrc(cardBrand)}
          alt={cardBrand}
          style={{ position: 'absolute', right: '10px', top: '50%', marginTop: '-10px' }}
        />
      </div>
    </Field>
  );
}

function useLabelMessage(): string {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Número do cartão';
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'cr':
    case 'gt':
    case 'sv':
    case 'pa':
    case 'do':
      return 'Número de la tarjeta';
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'eg':
    case 'gh':
    case 'jm':
      return 'Card number';
    default:
      throw new Error(`No message found for field card number in country ${country}`);
  }
}

function getMessage(validationError: UserInputValidationError, country: Country): ?string {
  switch (validationError.id) {
    case 'empty':
      return {
        br: 'Informe o número do cartão',
        mx: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        ar: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        co: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        cl: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        pe: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        ec: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        bo: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        uy: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        py: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        cr: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        pa: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        sv: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        gt: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        do: '¡Ey!, olvidaste introducir el número de tu tarjeta.',
        za: 'Please insert your card number',
        ke: 'Please insert your card number',
        ng: 'Please insert your card number',
        in: 'Please insert your card number',
        tz: 'Please insert your card number',
        ug: 'Please insert your card number',
        zm: 'Please insert your card number',
        eg: 'Please insert your card number',
        gh: 'Please insert your card number',
        jm: 'Please insert your card number',
      }[country];

    default:
      return {
        br: 'Número de cartão inválido',
        mx: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        ar: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        co: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        cl: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        pe: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        ec: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        bo: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        uy: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        py: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        cr: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        pa: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        gt: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        sv: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        do: 'El número es invalido. Confirma si no hay un error de escritura y vuelva a intentarlo.',
        za: 'Invalid card number',
        ke: 'Invalid card number',
        ng: 'Invalid card number',
        in: 'Invalid card number',
        tz: 'Invalid card number',
        ug: 'Invalid card number',
        zm: 'Invalid card number',
        eg: 'Invalid card number',
        gh: 'Invalid card number',
        jm: 'Invalid card number',
      }[country];
  }
}
